import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import employeeService from '../../services/employee.service';
import Toast from '../../components/Toast/Toast';
import './ViewEmployees.css';

const ViewEmployees = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ['SUPER_ADMIN', 'OFFICE_ADMIN', 'ADMIN'].includes(user?.role);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [toast, setToast] = useState(null);

  useEffect(() => { fetchEmployees(); }, [pagination.page, searchTerm]);

  const fetchEmployees = async () => {
    setLoading(true); setError(null);
    try {
      const isAdminRole = ['SUPER_ADMIN', 'OFFICE_ADMIN'].includes(user?.role);
      const companyId = isAdminRole
        ? localStorage.getItem('selectedCompanyId')
        : user?.companyId;

      const response = await employeeService.getAllEmployees(
        pagination.page, pagination.limit, searchTerm, companyId
      );
      
      if (response.success && response.data) {
        const employeesData = response.data.users || response.data.employees || response.data || [];
        const paginationData = response.data.pagination || response.pagination || {};
        setEmployees(Array.isArray(employeesData) ? employeesData : []);
        setPagination(prev => ({
          ...prev,
          total: paginationData.total || 0,
          totalPages: paginationData.totalPages || 0,
        }));
      } else { 
        setEmployees([]); 
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch employees');
      setEmployees([]);
    } finally { setLoading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this employee?')) return;
    try {
      const response = await employeeService.deleteEmployee(id);
      if (response.success) {
        fetchEmployees();
        setToast({ message: 'Employee deleted successfully!', type: 'success' });
      }
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to delete employee', type: 'error' });
    }
  };

  // Active/Inactive Toggle
 const handleToggleStatus = async (employeeId, currentStatus) => {
   

    const newStatus = currentStatus === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
    
   
    setEmployees(prevEmployees => 
      prevEmployees.map(emp => 
        emp.id === employeeId ? { ...emp, status: newStatus } : emp
      )
    );

    try {
      
      const response = await employeeService.updateEmployeeStatus(employeeId, { status: newStatus });
      
     
      if (response && response.success !== false) {
        setToast({ message: `Status updated to ${newStatus}!`, type: 'success' });
       
      } else {
      
        throw new Error('Failed from backend');
      }
    } catch (err) {
     
      setEmployees(prevEmployees => 
        prevEmployees.map(emp => 
          emp.id === employeeId ? { ...emp, status: currentStatus } : emp
        )
      );
      setToast({ message: err.response?.data?.message || 'Failed to update status', type: 'error' });
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  return (
    <div className="view-employees-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <div className="employees-header">
        <h4 className="employees-title">View Employees</h4>
        <button onClick={() => navigate('/employee/add')}>Add Employee</button>
      </div>

      <div className="search-container">
        <input type="text" placeholder="Search employees by name, email, code..."
          className="search-input" value={searchTerm} onChange={handleSearch} />
        <span className="search-icon">🔍</span>
      </div>

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading employees...</p>
        </div>
      ) : error ? (
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button className="btn-retry" onClick={fetchEmployees}>Retry</button>
        </div>
      ) : !employees || employees.length === 0 ? (
        <div className="empty-state">
          <p>No employees found</p>
          <button onClick={() => navigate('/employee/add')}>Add Employee</button>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="employees-table">
              <thead>
                <tr>
                  <th>EMPLOYEE CODE</th>
                  <th>NAME</th>
                  <th>EMAIL</th>
                  <th>ROLE</th>
                  <th>STATUS</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {employees.map(employee => (
                  <tr key={employee.id}>
                    <td>{employee.employeeCode || '-'}</td>
                    <td>{employee.firstName} {employee.lastName}</td>
                    <td>{employee.email}</td>
                    <td>{employee.role?.displayName || employee.role?.name || '-'}</td>
                    
                    
                 <td>
  {isAdmin ? (
    
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <label className="toggle-switch">
        <input
          type="checkbox"
          checked={employee.status === 'ACTIVE'}
          onChange={() => handleToggleStatus(employee.id, employee.status)}
        />
        <span className="toggle-slider"></span>
      </label>
      <span style={{ marginLeft: '8px', fontSize: '0.85rem' }}>
        {employee.status || 'ACTIVE'}
      </span>
    </div>
  ) : (
  
    <span className={`status-badge status-${employee.status?.toLowerCase()}`}>
      {employee.status || 'ACTIVE'}
    </span>
  )}
</td>

                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-action btn-view"
                          onClick={() => navigate(`/employee/view/${employee.id}`)}
                        >
                          View
                        </button>
                        <button
                          className="btn-action btn-edit"
                          onClick={() => navigate(`/employee/edit/${employee.id}`)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-action btn-delete"
                          onClick={() => handleDelete(employee.id)}
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button className="pagination-btn" disabled={pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}>
                Previous
              </button>
              <span className="pagination-info">
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </span>
              <button className="pagination-btn" disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}>
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ViewEmployees;